import React, { useCallback, useEffect, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { onboardStyles } from '../components/ComponentStyles';
import SkipConfirmation from '../components/SkipConfirmation';
import HelpModal from '../components/onboarding/HelpModal';
import OnboardErrorAlert from '../components/onboarding/OnboardErrorAlert';
import OnboardFooter from '../components/onboarding/OnboardFooter';
// Import components
import OnboardHeader from '../components/onboarding/OnboardHeader';
import TSPLoginFactory from '../components/onboarding/TSPLoginFactory';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import useOAuth from '../hooks/useOAuth';
// Import hooks
import useOnboardForm from '../hooks/useOnboardForm';
import useSkipOnboarding from '../hooks/useSkipOnboarding';
import { ServiceThemeOptions } from '../themes';
import {
  getTSPHelpTextByKey,
  getTSPImageNameByKey,
  getTSPNameByKey,
} from '../tsps';
import { handleOnboardError } from '../utils/onboardErrorHandling';

export default function TSPConfig() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const { tspKey } = useParams<{ tspKey: string }>();
  const location = useLocation();
  const { search } = location;

  // Extract theme values safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const textPrimaryColor = theme.palette?.text?.primary || '#000000';
  const textSecondaryColor = theme.palette?.text?.secondary || '#586166';

  // Use new centralized hooks
  const jwt = useJWT();
  const {
    approved,
    magicLink,
    motiveClientID,
    samsaraClientID,
    zubieClientID,
    bouncieClientID,
    TSP,
    updatingTSP,
    serviceName,
    redirectURIError,
    redirectURISuccess,
    setOnboarded,
    setRedirectURI,
    setTSP,
    setTspCount,
    tspCount,
  } = useAppState();

  // Get JWT-derived properties
  const token = jwt.rawToken || '';
  const isTokenValid = jwt.isValid;
  const fleetCode = jwt.fleetCode || '';
  const serviceId = jwt.serviceId;

  // Use tspKey from URL params or TSP from context
  const currentTSP = tspKey || TSP;

  // If we have a tspKey from URL but not set in context, update it
  useEffect(() => {
    if (tspKey && tspKey !== TSP) {
      setTSP(tspKey);
    }
  }, [tspKey, TSP, setTSP]);

  // Help modal state
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const handleHelpClick = useCallback(() => {
    amplitude.track('User Opened Help Dialog', {
      tsp: currentTSP,
      serviceName,
    });
    setHelpModalOpen(true);
  }, [currentTSP, serviceName]);

  // Use the skip onboarding hook (moved before useOnboardForm to avoid dependency issues)
  const {
    skipDialogOpen,
    handleSkipClick,
    handleSkipConfirmed,
    handleCloseSkipDialog,
  } = useSkipOnboarding({
    token,
    redirectURIError,
    serviceName,
    tsp: currentTSP,
  });

  // Use the onboard form hook
  const {
    isFormValid,
    setIsFormValid,
    disableButton,
    showError,
    errorText,
    errorDetails,
    setShowError,
    setErrorText,
    linkState,
    handleFormSubmit,
  } = useOnboardForm({
    TSP: currentTSP,
    serviceName,
    token,
    fleetCode,
    updatingTSP,
    serviceID: String(serviceId || ''),
    magicLink,
    redirectURISuccess,
    clientIDs: {
      motiveClientID,
      samsaraClientID,
      zubieClientID,
      bouncieClientID,
    },
    onSuccess: () => {
      setOnboarded(true);
      navigate('/success');
    },
    setRedirectURI,
    setTspCount,
    currentTspCount: tspCount, // Pass current TSP count for incrementing
    onSkip: handleSkipConfirmed, // Use the skip handler from useSkipOnboarding
  });

  // Use the OAuth redirects hook
  const oAuthHandlers = useOAuth({
    serviceName,
    linkState,
    clientIDs: {
      motiveClientID,
      samsaraClientID,
      zubieClientID,
      bouncieClientID,
    },
    onError: (error) => {
      setShowError(true);
      setErrorText(error);
    },
  });

  // Validate token and approval on mount.
  useEffect(() => {
    if (!isTokenValid) {
      handleOnboardError(
        { message: 'Invalid token', response: { status: 401 } },
        { tsp: currentTSP, serviceName },
        { navigate, setShowError, setErrorText, search }
      );
    } else if (!approved) {
      navigate('/');
    }
  }, [
    isTokenValid,
    approved,
    navigate,
    currentTSP,
    serviceName,
    search,
    setShowError,
    setErrorText,
  ]);

  // Handle back button click.
  const handleBackClick = () => {
    // Check if user came from updateOrAdd page
    const urlParams = new URLSearchParams(search);
    const fromPage = urlParams.get('from');

    if (fromPage === 'updateOrAdd') {
      navigate('/updateOrAdd', { replace: true });
    } else {
      navigate('/selectTSP', { replace: true });
    }
  };

  // Get TSP info
  const TSPName = getTSPNameByKey(currentTSP) || '';
  const TSPHelpText = getTSPHelpTextByKey(currentTSP);
  const TSPImageName = getTSPImageNameByKey(currentTSP) || '';

  // If we don't have a valid TSP, redirect to the select page
  if (!currentTSP || !TSPName) {
    return <Navigate to='/selectTSP' />;
  }

  return (
    <>
      <Box sx={onboardStyles.mainContainer}>
        <Paper
          elevation={3}
          sx={{
            ...onboardStyles.paper,
            bgcolor: paperBgColor,
          }}
        >
          {/* Header with back button and logo */}
          <OnboardHeader
            onBackClick={handleBackClick}
            imageName={TSPImageName}
            tsp={currentTSP}
            serviceName={serviceName}
            backgroundColor={paperBgColor}
          />

          {/* Title and subtitle */}
          <Box sx={onboardStyles.titleContainer}>
            <Typography
              variant='h5'
              align='center'
              sx={{
                ...onboardStyles.title(theme as any),
                color: textPrimaryColor,
              }}
            >
              Add your {TSPName} account
            </Typography>

            <Typography
              variant='body2'
              align='center'
              sx={{
                ...onboardStyles.subtitle(theme as any),
                color: textSecondaryColor,
              }}
            >
              By providing your {TSPName} credentials to Axle, you're enabling
              Axle to retrieve your telematics data.
            </Typography>
          </Box>

          {/* Form area */}
          <FormContainer
            defaultValues={{
              username: '',
              password: '',
              database: '',
              apikey: '',
              authorizationcode: '',
              carriername: '',
              FTPUsername: '',
              FTPPassword: '',
              skipCredentials: false,
              dotnumber: '',
            }}
            onSuccess={handleFormSubmit}
            FormProps={{
              autoComplete: 'off',
              id: 'onboarding-form',
            }}
          >
            <Box sx={onboardStyles.formContainer}>
              {/* Error alert */}
              {showError && (
                <OnboardErrorAlert
                  errorText={errorText}
                  helpText={errorDetails?.helpText}
                  showEscapeHatch={errorDetails?.showEscapeHatch}
                  tsp={currentTSP}
                  serviceName={serviceName}
                  onClose={() => setShowError(false)}
                  onSkip={handleSkipClick}
                  onHelpClick={handleHelpClick}
                />
              )}

              {/* Login component factory determines which fields to display */}
              <TSPLoginFactory
                tsp={currentTSP}
                onValidityChange={setIsFormValid}
                TSP={currentTSP}
                serviceName={serviceName}
                onHelpClick={handleHelpClick}
                oAuthHandlers={oAuthHandlers}
              />
            </Box>

            {/* Footer with Continue button and Skip link */}
            <OnboardFooter
              disableButton={disableButton || !isFormValid}
              isFormValid={isFormValid}
              tsp={currentTSP}
              primaryColor={primaryColor}
              onHelpClick={handleHelpClick}
            />
          </FormContainer>
        </Paper>
      </Box>

      {/* Help modal */}
      {TSPHelpText && (
        <HelpModal
          open={helpModalOpen}
          onClose={() => setHelpModalOpen(false)}
          helpText={TSPHelpText}
          tsp={currentTSP}
          serviceName={serviceName || ''}
        />
      )}

      {/* Skip confirmation dialog */}
      {skipDialogOpen && (
        <SkipConfirmation
          open={skipDialogOpen}
          onClose={handleCloseSkipDialog}
          onSkip={handleSkipConfirmed}
          serviceName={serviceName || ''}
        />
      )}
    </>
  );
}
